import React, { useEffect, useState } from 'react';
import { FileImage, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ewbApi } from '@/services/ewayBillApi';
import { DocUploadButton } from '@/components/ewayBill/DocUploadButton';

const HINT = {
  customer_invoice: 'Upload the bulk Sales Invoice PDF here or in Import Data — each invoice becomes an e-way bill on its own; held ones show under Needs attention.',
  outside_invoice: 'Sales invoices NOT done in WMS: upload the invoice PDF (many invoices in one file is fine). Goods lines come from the HSN table below the Grand Total, the customer from Customer Master (Bill To code), the transporter from the invoice. Each one becomes an e-way bill for the GST portal JSON on its own; held ones show under Needs attention.',
  transfer_out: 'Upload the Ginesys Transfer Out PDF — each Transfer Out (EBO) is checked against its pick slip (Challan No) and becomes an e-way bill on its own; held ones show under Needs attention.',
  direct_ship: 'Inhouse Direct Ship to our sites: upload the Ginesys Transfer Out made for the shipment, tick the boxes that went with it (pieces must match), then fetch and generate here — it never goes out on its own.',
  manual_dc: 'Upload a photo / scan of the manual DC — the AI fills the form, you check it.',
  inward: 'Goods we receive: upload a photo / PDF of the supplier bill (GST registered or not) or the job worker\'s return DC — the AI fills the form, you check it. Goes out as the GST portal JSON (Inward).',
};
const UPLOAD_BTN = { manual_dc: ['ewb-manual-new-btn', 'Upload manual DC'], inward: ['ewb-inward-new-btn', 'Upload supplier bill / jobber DC'],
  direct_ship: ['ewb-direct-ship-new-btn', 'Transfer Out & boxes'] };
const LIST_LABEL = { manual_dc: 'Saved manual DCs', inward: 'Saved inward bills', direct_ship: 'Direct Ship Transfer Outs', outside_invoice: 'Uploaded invoices (not in WMS)' };
const NO_LABEL = { customer_invoice: 'Invoice number', outside_invoice: 'Invoice number', inward: 'Supplier bill / DC number', direct_ship: 'Direct Ship Transfer Out number' };

const DocRow = ({ d, active, onPick }) => (
  <button type="button" onClick={() => onPick(d)}
    className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-amber-50 ${active ? 'bg-amber-50' : ''}`}
    data-testid={`ewb-doc-pick-${d.doc_no}${d.source?.kind === 'ewb_inward_docs' ? `-${d.source.ref}` : ''}`}>
    <span className="font-mono font-semibold">{d.doc_no}</span>
    <span className="truncate text-gray-600">{d.party}</span>
    {d.note && <span className="shrink-0 rounded-full bg-slate-100 px-2 text-[11px] text-slate-600">{d.note}</span>}
    <span className="ml-auto shrink-0 text-xs text-gray-500">{d.date}{d.vehicle_no ? ` · ${d.vehicle_no}` : ''}</span>
    {d.ewb_no && <span className="shrink-0 rounded-full bg-emerald-100 px-2 text-[11px] font-semibold text-emerald-800">EWB {d.ewb_no}</span>}
  </button>
);

/** Step 1 — document type + number, recent documents to pick from, and the PDF / manual DC uploads. */
export const DocPicker = ({ cfg, docType, setDocType, docNo, setDocNo, onPick, onManual, pickedRef, onUploaded }) => {
  const [docs, setDocs] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    setDocs(null);
    const t = setTimeout(() => ewbApi.documents({ doc_type: docType, q: docNo }).then(setDocs).catch(() => setDocs([])), 300);
    return () => clearTimeout(t);
  }, [docType, docNo, tick]);
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Document type</Label>
          <select value={docType} onChange={(e) => setDocType(e.target.value)} className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm" data-testid="ewb-doc-type">
            {(cfg?.doc_types || []).map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <Label>{NO_LABEL[docType] || 'Document / DC number'}</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <Input value={docNo} onChange={(e) => setDocNo(e.target.value)} placeholder="Type or pick below" className="pl-9 font-mono" data-testid="ewb-doc-no" />
          </div>
        </div>
      </div>
      {HINT[docType] && (
        <div className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2">
          <p className="text-xs text-gray-600 pt-1.5" data-testid="ewb-doc-hint">{HINT[docType]}</p>
          {UPLOAD_BTN[docType]
            ? <Button size="sm" variant="outline" onClick={onManual} data-testid={UPLOAD_BTN[docType][0]}><FileImage className="w-4 h-4 mr-1" />{UPLOAD_BTN[docType][1]}</Button>
            : <DocUploadButton key={docType} kind={docType} onDone={() => { setTick((n) => n + 1); onUploaded?.(); }} />}
        </div>
      )}
      <div>
        <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{LIST_LABEL[docType] || 'Recent documents in WMS'}</div>
        <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 divide-y bg-white" data-testid="ewb-doc-list">
          {docs === null ? <Loader2 className="w-5 h-5 mx-auto my-6 animate-spin text-amber-600" />
            : docs.length === 0 ? <p className="py-6 text-center text-sm text-gray-500" data-testid="ewb-doc-list-empty">No matching documents — you can still type the number</p>
              : docs.map((d) => <DocRow key={`${d.doc_no}-${d.source?.ref}`} d={d} active={d.doc_no === docNo && (!pickedRef || d.source?.ref === pickedRef)} onPick={onPick} />)}
        </div>
      </div>
    </div>
  );
};

export default DocPicker;
